import { useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardHeader,
  Field,
  Select,
  SelectItem,
  SelectTrigger,
} from '@billda/design-system';
import { UserStatus, type UserRoleDto } from '../../../api/userManagementApi';
import { useAssignableRoles } from '../../../hooks/useAssignableRoles';
import { useUpdateUserRole } from '../../../hooks/useUpdateUserRole';
import { PermissionGate } from '../../../../../shared/components/gates/PermissionGate';
import { Permissions } from '@billda/permissions-ui';
import { canEditUserRole } from '../../../utils/roleChangePolicy';

interface UserRoleCardProps {
  userId: string;
  roles: UserRoleDto[];
  status: UserStatus;
  isCurrentUser?: boolean;
  isEditing: boolean;
  onEditingChange: (editing: boolean) => void;
}

export function UserRoleCard({
  userId,
  roles,
  status,
  isCurrentUser,
  isEditing,
  onEditingChange,
}: UserRoleCardProps) {
  const activeRole = roles.length === 1 ? roles[0] : undefined;

  const [selectedRoleId, setSelectedRoleId] = useState<number | ''>(
    activeRole?.roleId ?? ''
  );
  const [error, setError] = useState<string | null>(null);
  const editButtonRef = useRef<HTMLButtonElement>(null);

  const {
    assignableRoles,
    canManageAdmins,
    isLoading: rolesLoading,
  } = useAssignableRoles({ enabled: isEditing });
  const canEditRole = canEditUserRole({
    activeRole,
    status,
    isCurrentUser: !!isCurrentUser,
    canManageAdmins,
  });

  const updateMutation = useUpdateUserRole();

  const handleEdit = () => {
    onEditingChange(true);
    setSelectedRoleId(activeRole?.roleId ?? '');
    setError(null);
  };

  const finishEditing = () => {
    onEditingChange(false);
    requestAnimationFrame(() => editButtonRef.current?.focus());
  };

  const handleCancel = () => {
    setSelectedRoleId(activeRole?.roleId ?? '');
    setError(null);
    finishEditing();
  };

  const handleSave = () => {
    if (selectedRoleId === '' || selectedRoleId === activeRole?.roleId) {
      finishEditing();
      return;
    }

    updateMutation.mutate(
      { userId, roleId: selectedRoleId },
      {
        onSuccess: () => {
          setError(null);
          finishEditing();
        },
        onError: error => {
          setError(
            error instanceof Error
              ? error.message
              : 'Failed to update user role'
          );
        },
      }
    );
  };

  return (
    <Card data-testid="user-role-card">
      <CardHeader>
        <h3 className="type-h6">Role</h3>
        {!isEditing && canEditRole && (
          <PermissionGate permission={Permissions.Users.Manage} fallback={null}>
            <Button
              ref={editButtonRef}
              variant="ghost"
              size="sm"
              onClick={handleEdit}
              data-testid="user-role-edit-button"
            >
              Edit
            </Button>
          </PermissionGate>
        )}
      </CardHeader>
      <CardContent className="pt-4">
        <div className="flex flex-col gap-4">
          {isEditing ? (
            <div data-testid="user-role-edit-mode">
              <div className="flex flex-col gap-4">
                {error && (
                  <Alert severity="error" onClose={() => setError(null)}>
                    {error}
                  </Alert>
                )}

                <Field label="Select role">
                  <Select
                    value={
                      selectedRoleId === '' ? undefined : String(selectedRoleId)
                    }
                    onValueChange={value => setSelectedRoleId(Number(value))}
                    disabled={updateMutation.isPending || rolesLoading}
                    data-testid="user-role-select"
                  >
                    <SelectTrigger />
                    {assignableRoles.map(role => (
                      <SelectItem key={role.id} value={String(role.id)}>
                        {role.name}
                      </SelectItem>
                    ))}
                  </Select>
                </Field>

                <div className="flex justify-end gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleCancel}
                    disabled={updateMutation.isPending}
                    data-testid="user-role-cancel-button"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="neutral"
                    size="sm"
                    onClick={handleSave}
                    disabled={
                      updateMutation.isPending ||
                      selectedRoleId === '' ||
                      selectedRoleId === activeRole?.roleId
                    }
                    data-testid="user-role-save-button"
                  >
                    {updateMutation.isPending ? 'Saving...' : 'Save changes'}
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <>
              {error && (
                <Alert severity="error" onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}
              <span
                className="type-body-md font-medium"
                data-testid="user-role-display"
              >
                {activeRole?.roleName ?? 'No role assigned'}
              </span>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
